import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Upload,
  ScanSearch,
  Globe,
  Gauge,
  PenLine,
  CalendarRange,
  ArrowRight,
  ShieldCheck,
  Sparkles,
  BarChart3,
  Lightbulb,
  FileDown,
  Lock,
  CheckCircle2,
  Quote,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { AppShell } from '../components/Layout';
import SeoHead from '../components/seo/SeoHead';

const STEPS = [
  {
    n: '01',
    icon: Upload,
    title: 'Ingest',
    short: 'PDF upload or manual paste',
    detail:
      'Drop your LinkedIn "Save to PDF" export or fill our 6-step wizard. Everything stays on your account, gated behind a required consent checkbox.',
  },
  {
    n: '02',
    icon: ScanSearch,
    title: 'Extract',
    short: 'Structured profile understanding',
    detail:
      'We parse your headline, About, every role, your skills, education, and recent posts into a clean structured object using a fast heuristic parser (free tier) or an AI parser with deterministic safety net (paid tier).',
  },
  {
    n: '03',
    icon: Globe,
    title: 'Research',
    short: 'Trending topics + thought leaders*',
    detail:
      'For your role + industry, we surface 20 currently-trending topic ideas. Peer & thought-leader benchmarking is part of the Enterprise Plan.*',
  },
  {
    n: '04',
    icon: Gauge,
    title: 'Score',
    short: 'Current vs potential (0–100)',
    detail:
      'Hybrid scoring engine: deterministic rules (length, completeness, outcomes, keywords) blended 50/50 with AI evaluation across 5 categories, including a Potential score that shows your ceiling.',
  },
  {
    n: '05',
    icon: PenLine,
    title: 'Generate',
    short: '10 voice-matched opinion posts',
    detail:
      "We extract your voice (rhythm, vocab, openers, closers) and write opinion-driven posts with a clear hook → stance → 1-2 insights → conclusion. You can regenerate with your own topics anytime.",
  },
  {
    n: '06',
    icon: CalendarRange,
    title: 'Schedule',
    short: 'Build a publishable content calendar',
    detail:
      'Pick the posts you love, choose a cadence (daily / 3x / 2x / weekly / bi-weekly), and export the schedule to Excel, Word, or PDF for your team or VA.',
  },
];

const PRINCIPLES = [
  {
    icon: BarChart3,
    title: 'Hybrid scoring, not vibes',
    body:
      "We don't trust a single number from an AI. Every score is the blend of deterministic rules and AI judgement, so it's stable across runs and explainable.",
  },
  {
    icon: Lightbulb,
    title: 'Voice over template',
    body:
      "Generic templates die in feed. We extract YOUR voice from posts you've actually written and force every generated post to mirror it.",
  },
  {
    icon: ShieldCheck,
    title: 'Opinion, not advice',
    body:
      "Every post takes a clear stance with 1–2 defensible insights and a thought-provoking close. No 'thoughts?' filler.",
  },
  {
    icon: Lock,
    title: 'Privacy by default',
    body:
      "Your analyses are stored only in your account. Admins/editors can collaborate, but viewers stay isolated to their own data.",
  },
];

const SCORE_CATEGORIES = [
  { name: 'Headline', rule: 'Length, value-prop signal, identity blocks, outcome keywords' },
  { name: 'About', rule: 'Word count window, first-person, metrics, action verbs, CTA' },
  { name: 'Experience', rule: 'Role count, bullet quality, outcome verbs, embedded metrics' },
  { name: 'Skills', rule: 'Count window, diversity, high-signal terms' },
  { name: 'Engagement Hooks', rule: 'Post cadence, hook openers, questions, short-line punch' },
];

const EXPORTS = [
  {
    icon: FileDown,
    name: 'Profile report (PDF / DOCX)',
    detail: 'Full blueprint: scores, gaps, headline variants, About rewrite, experience bullets, skills.',
  },
  {
    icon: CalendarRange,
    name: 'Content calendar (XLSX / DOCX / PDF)',
    detail: 'Selected posts mapped to dates with day themes, topic, and estimated engagement.',
  },
];

export default function MethodologyPage() {
  const navigate = useNavigate();

  return (
    <AppShell>
      <SeoHead
        title={"Methodology — how Blayzrr scores LinkedIn profiles"} 
        description={"Blayzrr's Profile Intelligence Framework: discoverability, positioning, credibility, quantified impact, completeness and objective-specific readiness \u2014 scored deterministically."} 
        path={"/methodology"} 
      />
      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="methodology-hero">
        <div className="absolute inset-0 hero-mesh-light opacity-60 pointer-events-none" aria-hidden />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-10">
          <Badge className="bg-card border border-border text-foreground" data-testid="methodology-eyebrow">
            <Sparkles size={12} className="mr-1.5" /> Methodology
          </Badge>
          <h1 className="mt-4 text-4xl sm:text-5xl font-bold leading-[1.05] max-w-3xl">
            How Blayzrr turns your LinkedIn into a lead engine.
          </h1>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-2xl">
            A look under the hood. We combine deterministic checks with AI judgement so every
            recommendation is grounded in evidence, your voice, and what is currently working on LinkedIn.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button onClick={() => navigate('/dashboard')} className="rounded-xl" data-testid="methodology-cta-analyze">
              Run my analysis <ArrowRight size={14} className="ml-2" />
            </Button>
            <Button variant="secondary" onClick={() => navigate('/dashboard')} className="rounded-xl" data-testid="methodology-cta-back">
              Back to dashboard
            </Button>
          </div>
        </div>
      </section>

      {/* Step diagram */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="methodology-steps">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
          <div>
            <p className="text-xs font-mono-edge text-primary tracking-[0.16em]">THE PIPELINE</p>
            <h2 className="text-2xl sm:text-3xl font-semibold mt-1">Six stages, one pass.</h2>
          </div>
          <p className="text-sm text-muted-foreground max-w-md">
            Every analysis runs the full pipeline. You can rerun any stage (regenerate posts, refresh inspiration,
            rebuild calendar) without touching the others.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {STEPS.map((s, idx) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.32, delay: idx * 0.05, ease: [0.2, 0.8, 0.2, 1] }}
              >
                <Card className="h-full border-border hover:border-primary/40 transition-colors" data-testid={`methodology-step-${s.n}`}>
                  <CardContent className="p-5 h-full flex flex-col">
                    <div className="flex items-center justify-between mb-3">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                        <Icon size={18} />
                      </span>
                      <span className="font-mono-edge text-xs text-muted-foreground">{s.n}</span>
                    </div>
                    <h3 className="text-lg font-semibold">{s.title}</h3>
                    <p className="text-sm text-primary font-medium mt-0.5">{s.short}</p>
                    <p className="text-sm text-muted-foreground mt-2 flex-1">{s.detail}</p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Principles */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 border-t border-border" data-testid="methodology-principles">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-8">
          <div>
            <p className="text-xs font-mono-edge text-primary tracking-[0.16em]">PRINCIPLES</p>
            <h2 className="text-2xl sm:text-3xl font-semibold mt-1">What we believe makes profiles win.</h2>
            <p className="text-sm text-muted-foreground mt-3">
              The product is opinionated. Here's where those opinions come from and how they shape the output.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {PRINCIPLES.map((p) => {
              const Icon = p.icon;
              return (
                <Card key={p.title} className="border-border">
                  <CardContent className="p-5">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-secondary text-primary mb-3">
                      <Icon size={16} />
                    </span>
                    <h3 className="font-semibold">{p.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1.5">{p.body}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Scoring deep dive */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 border-t border-border" data-testid="methodology-scoring">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-8">
          <div>
            <p className="text-xs font-mono-edge text-primary tracking-[0.16em]">SCORING DEEP DIVE</p>
            <h2 className="text-2xl sm:text-3xl font-semibold mt-1">How current vs potential is calculated.</h2>
            <p className="text-sm text-muted-foreground mt-3">
              Two scores. One number you have today. One number you can reach by adopting every recommendation.
              The delta is your upside.
            </p>
            <div className="mt-5 rounded-xl border border-border bg-card p-4">
              <p className="text-xs font-mono-edge text-muted-foreground mb-2">FORMULA (per category)</p>
              <pre className="font-mono-edge text-[12px] leading-relaxed whitespace-pre-wrap">
{`score(category) =
  0.5 × rule_check(profile_text)
+ 0.5 × ai_judgement(profile_text)  // peer comparison* on Enterprise

overall =
  0.23·headline + 0.27·about + 0.33·experience + 0.17·skills

engagement_readiness = engagement_hooks  (shown separately)`}
              </pre>
            </div>
          </div>

          <div>
            <Card className="border-border">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                    <Gauge size={16} />
                  </span>
                  <h3 className="font-semibold">What each category measures</h3>
                </div>
                <ul className="divide-y divide-border">
                  {SCORE_CATEGORIES.map((c) => (
                    <li key={c.name} className="py-3 first:pt-0 last:pb-0">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 size={14} className="text-primary mt-1 shrink-0" />
                        <div>
                          <p className="text-sm font-medium">{c.name}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{c.rule}</p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-primary/30 bg-primary/5 mt-4">
              <CardContent className="p-5 flex gap-3">
                <Quote size={16} className="text-primary mt-0.5 shrink-0" />
                <p className="text-sm">
                  <span className="font-medium">Why hybrid?</span> Pure LLM scores wobble between runs. Pure
                  rule scores miss nuance. Blending gives stability AND judgement — the same combo a great
                  career coach uses.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Voice & posts */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 border-t border-border" data-testid="methodology-voice">
        <div className="grid lg:grid-cols-2 gap-8">
          <div>
            <p className="text-xs font-mono-edge text-primary tracking-[0.16em]">VOICE EXTRACTION</p>
            <h2 className="text-2xl sm:text-3xl font-semibold mt-1">We don't write like AI. We write like you.</h2>
            <p className="text-sm text-muted-foreground mt-3">
              From the posts you've already shared, we extract sentence rhythm, vocabulary level, emoji usage,
              jargon density, openers, closers, and signature traits. Every generated post must match these.
            </p>
            <ul className="text-sm space-y-2 mt-4">
              {['Sentence style + average words per sentence', 'Emoji & jargon levels', 'Opening + closing patterns', 'Personal vs professional tone balance', 'Themes you have already covered (so we don\'t repeat)'].map((t) => (
                <li key={t} className="flex gap-2"><span className="text-primary">•</span> {t}</li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-xs font-mono-edge text-primary tracking-[0.16em]">POST STRUCTURE</p>
            <h2 className="text-2xl sm:text-3xl font-semibold mt-1">Hook → Stance → Insights → Punchline.</h2>
            <p className="text-sm text-muted-foreground mt-3">
              Every post is opinion-driven and follows the same proven structure so it earns the click on "see more"
              and lands a memorable close.
            </p>
            <ol className="text-sm space-y-2 mt-4">
              {[
                ['HOOK', 'A sharp first line that earns the click before \u201csee more\u201d.'],
                ['CONTEXT', 'A one-liner that anchors the reader in the trend.'],
                ['STANCE', 'A clear opinion. Pick a side. Don\u2019t hedge.'],
                ['INSIGHTS', '1–2 defensible insights with proof (a number, a story, a contrast).'],
                ['CONCLUSION', 'A provocation, prediction, or question — never a generic \u201cThoughts?\u201d'],
              ].map(([k, v]) => (
                <li key={k} className="flex gap-3">
                  <span className="inline-flex h-6 px-2 items-center justify-center rounded font-mono-edge text-[10px] bg-secondary text-foreground border border-border">{k}</span>
                  <span className="text-muted-foreground">{v}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Calendar logic */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 border-t border-border" data-testid="methodology-calendar">
        <div className="grid lg:grid-cols-[2fr_1fr] gap-8 items-start">
          <div>
            <p className="text-xs font-mono-edge text-primary tracking-[0.16em]">CONTENT CALENDAR</p>
            <h2 className="text-2xl sm:text-3xl font-semibold mt-1">From great drafts to a publishable plan.</h2>
            <p className="text-sm text-muted-foreground mt-3 max-w-2xl">
              You pick the posts you'll actually publish. We assign them across a cadence with themed days,
              and export the result for your team or VA. Calendar generation is deterministic — same inputs always
              produce the same schedule.
            </p>

            <div className="grid sm:grid-cols-2 gap-3 mt-5">
              {[
                ['Daily (Mon–Fri)', 'Aggressive growth pushes'],
                ['3× / week', 'The sweet spot for most leaders'],
                ['2× / week', 'Sustainable for busy execs'],
                ['Weekly', 'Quality over quantity'],
                ['Bi-weekly', 'Slow-burn thought leadership'],
                ['Themed days', 'Opinion Day, Story Day, Framework Day…'],
              ].map(([k, v]) => (
                <div key={k} className="rounded-lg border border-border bg-card p-3">
                  <p className="text-sm font-medium">{k}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{v}</p>
                </div>
              ))}
            </div>
          </div>

          <Card className="border-border">
            <CardContent className="p-5">
              <p className="text-xs font-mono-edge text-muted-foreground mb-2">EXPORTS</p>
              <ul className="space-y-3">
                {EXPORTS.map((e) => {
                  const Icon = e.icon;
                  return (
                    <li key={e.name} className="flex gap-3">
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-secondary text-primary shrink-0">
                        <Icon size={16} />
                      </span>
                      <div>
                        <p className="text-sm font-medium">{e.name}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{e.detail}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Privacy + footer CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 border-t border-border" data-testid="methodology-privacy">
        <Card className="border-border overflow-hidden">
          <CardContent className="p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
            <div className="flex items-start gap-4">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground shrink-0">
                <Lock size={20} />
              </span>
              <div>
                <h3 className="text-xl font-semibold">Your profile stays yours.</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                  Analyses are scoped to your account. Admins/editors can collaborate across the team;
                  viewers are isolated. You can delete an analysis any time from the dashboard.
                </p>
              </div>
            </div>
            <Button onClick={() => navigate('/dashboard')} className="rounded-xl shrink-0" data-testid="methodology-footer-cta">
              Start a new analysis <ArrowRight size={14} className="ml-2" />
            </Button>
          </CardContent>
        </Card>
      </section>
    </AppShell>
  );
}
